import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  TextareaAutosize,
  TextField,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import SendToManagerModal from './SendToManagerModal'
import { useDispatch } from 'react-redux'
import { enqueueSnackbar } from '../../actions/notification.actions'
import wizardService from '../../services/wizard.service'
import {
  caseTrackingCommunicationSubmissionTypeEnum,
  caseTrackingCommunicationVisibilityTypeEnum,
} from '../../constants/communication.constants'

const useStyles = makeStyles(theme => ({
  root: {
    '& label.Mui-disabled': {
      color: 'black',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: 'black',
    },
  },
}))

const SendToManager = ({
  caseParticipants,
  caseId,
  onClose,
  open,
  documentData,
  saveDraft,
}) => {
  const classes = useStyles()
  const { activeCaseParticipants } = caseParticipants
  const [activeCaseParticipantsList, setActiveCaseParticipantsList] = useState(
    activeCaseParticipants
  )
  const numberOfRandomCharacters = 30
  const [randomCode, setRandomCode] = useState('')
  const [selectedParticipant, setSelectedParticipant] = useState(null)
  const [openModalStepTwo, setOpenModalStepTwo] = useState(false)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const baseUrl = window.location.origin

  const [emailSubject, setEmailSubject] = useState(
    `Case ${caseId}: ${documentData.documentName}`
  )
  const [customMessage, setCustomMessage] = useState('')
  const [
    lastEntrySendToManagerDetails,
    setLastEntrySendToManagerDetails,
  ] = useState(null)

  const generateRandomString = length => {
    let randomString = Math.random().toString(36).substring(2)
    while (randomString.length < length) {
      randomString += Math.random().toString(36).substring(2)
    }

    return randomString.substring(0, length)
  }

  const displayBody = `Hi ${
    selectedParticipant?.personName || 'Participant'
  },\n\nPlease find below a link to complete the letter ${
    documentData.documentName
  }\n\nLink: ${baseUrl}/wizard/docServiceUser?code=${randomCode}`

  const body = `Hi ${selectedParticipant?.personName || 'Participant'},<br><br>
    Please find below a link to complete the letter <strong>${
      documentData.documentName
    }</strong>.<br><br>
    <a href="${baseUrl}/wizard/docServiceUser?code=${randomCode}">Click here to access the letter</a> <br><br>`

  const [emailBody, setEmailBody] = useState(body)

  useEffect(() => {
    setRandomCode(generateRandomString(numberOfRandomCharacters))
  }, [])

  useEffect(() => {
    if (lastEntrySendToManagerDetails !== null) {
      var previousSelectedRecipient = activeCaseParticipantsList?.find(
        c =>
          c.participantUserId ===
          lastEntrySendToManagerDetails.sendToManagerSentToUserId
      )

      if (previousSelectedRecipient) {
        previousSelectedRecipient.personEmail =
          lastEntrySendToManagerDetails.emailSentTo
        previousSelectedRecipient.emailRecipientType = 'To'

        setActiveCaseParticipantsList(
          activeCaseParticipantsList.map(p =>
            p.participantUserId === previousSelectedRecipient.participantUserId
              ? {
                  ...p,
                  personEmail: lastEntrySendToManagerDetails.emailSentTo,
                  emailRecipientType: 'To',
                }
              : {
                  ...p,
                  emailRecipientType:
                    p.emailRecipientType === 'To' ? '' : p.emailRecipientType, // resets all others to ''
                }
          )
        )
      }
    } else {
      setActiveCaseParticipantsList(activeCaseParticipantsList)
    }
  }, [activeCaseParticipants, lastEntrySendToManagerDetails])

  useEffect(() => {
    if (selectedParticipant) {
      setEmailBody(displayBody)
    }

    if (documentData.documentGuid) {
      const fetchSendToManager = async () => {
        getSendToManager(documentData.documentGuid)
      }

      fetchSendToManager()
    }
  }, [selectedParticipant, documentData])

  const handleParticipantSelect = participant => {
    setSelectedParticipant(participant)
    setOpenModalStepTwo(true)
  }

  const escapeHtml = text => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  const getSendToManager = async documentGuid => {
    try {
      const data = await wizardService.getLastEntrySendToManager(documentGuid)

      if (data !== null) {
        setCustomMessage(data.customMessage)
        setLastEntrySendToManagerDetails(data)
      }
    } catch (error) {
      console.error('Error fetching last entry:', error)
    }
  }

  const handleSend = () => {
    setLoading(true)

    const customMessageHtml = escapeHtml(customMessage || '').replace(
      /\n/g,
      '<br>'
    )

    const fullEmailBody = `${body}${customMessageHtml}`

    const activeCaseParticipantsEmailModel = [
      {
        email: selectedParticipant.personEmail,
        emailRecipientType: 2,
        sentToUserId: selectedParticipant.participantUserId,
      },
    ]
    const sendToManagerDetails = {
      documentGuid: documentData.documentGuid,
      sendToManagerCode: randomCode,
      sendToManagerSentToUserId: selectedParticipant.participantUserId,
      emailSentTo: selectedParticipant.personEmail,
      customMessage: customMessage,
    }

    const model = {
      activeCaseParticipantsEmailModel,
      sendToManagerDetails,
      communicationSubmissionTypeId:
        caseTrackingCommunicationSubmissionTypeEnum.RESPONSE_NOT_REQUIRED,
      communicationVisibilityType:
        caseTrackingCommunicationVisibilityTypeEnum.Public,
      subject: emailSubject,
      body: fullEmailBody,
    }

    wizardService
      .createCommunicationViaSendToManager(caseId, model)
      .then(() => {
        dispatch(
          enqueueSnackbar({
            message:
              'The email has been sent and posted to the Communications module.',
            options: {
              variant: 'success',
            },
          })
        )
        setOpenModalStepTwo(false)
        saveDraft()
        getSendToManager(documentData.documentGuid)
        onClose()
      })
      .catch(error => {
        console.error('Failed to send email:', error)
        dispatch(
          enqueueSnackbar({
            message: 'Failed to send email. Please try again.',
            options: { variant: 'error' },
          })
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handlePrevious = () => {
    setOpenModalStepTwo(false)
  }

  const handleCancel = () => {
    setOpenModalStepTwo(false)
  }

  return (
    <div>
      {activeCaseParticipantsList && (
        <>
          <SendToManagerModal
            open={open}
            onClose={onClose}
            participants={activeCaseParticipantsList}
            handleSubmit={handleParticipantSelect}
            lastEntrySendToManagerDetails={lastEntrySendToManagerDetails}
          />

          {selectedParticipant && openModalStepTwo && (
            <Dialog
              open={openModalStepTwo}
              onClose={handleCancel}
              fullWidth
              maxWidth='md'>
              <DialogTitle>Update Email Content</DialogTitle>
              <DialogContent>
                <TextField
                  className={classes.root}
                  inputProps={{ 'aria-label': 'Subject' }}
                  label='Subject'
                  value={emailSubject}
                  fullWidth
                  disabled
                  margin='normal'
                />
                <TextareaAutosize
                  aria-label='Email body'
                  minRows={6}
                  value={emailBody}
                  disabled
                  style={{ width: '100%', marginTop: '10px' }}
                />
                <TextareaAutosize
                  aria-label='Custom message'
                  minRows={4}
                  placeholder='Add your custom message here...'
                  value={customMessage}
                  onChange={e => {
                    setCustomMessage(e.target.value)
                  }}
                  style={{ width: '100%', marginTop: '10px' }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCancel}
                  color='secondary'
                  aria-label='Cancel'>
                  Cancel
                </Button>
                <Button
                  onClick={handlePrevious}
                  color='primary'
                  aria-label='Previous'>
                  Previous
                </Button>
                <Button
                  onClick={handleSend}
                  color='primary'
                  aria-label='Send'
                  disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : 'Send'}
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </>
      )}
    </div>
  )
}

export default SendToManager
